import React, { useEffect, useState } from "react";
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  message,
  Space,
  Tag,
  Popconfirm,
} from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined, ReloadOutlined } from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import { LoginUserService } from "./Master_LoginUserService";
import {
  LoginUser,
  CreateUserRequest,
  UpdateUserRequest,
  UserRole,
  ADMIN_ROLE,
  STANDARD_ROLE,
} from "./web_parts/types";

const { Option } = Select;

const roleMap: Record<UserRole, { name: string; color: string }> = {
  [ADMIN_ROLE]: { name: "管理者", color: "red" },
  [STANDARD_ROLE]: { name: "標準ユーザー", color: "blue" },
};

const Master_LoginUser: React.FC = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const [users, setUsers] = useState<LoginUser[]>([]);
  const [filteredData, setFilteredData] = useState<LoginUser[]>([]);
  const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
  const [modalMode, setModalMode] = useState<"create" | "edit">("create");
  const [selectedUser, setSelectedUser] = useState<LoginUser | null>(null);
  const [form] = Form.useForm();

  const [isFilterDialogVisible, setIsFilterDialogVisible] = useState(false);
  const [maxRecords, setMaxRecords] = useState<number>(50);
  const [filterConditions, setFilterConditions] = useState({
    email: "",
    displayName: "",
    userId: "",
    role: null as UserRole | null,
  });

  const [isPasswordModalVisible, setIsPasswordModalVisible] = useState<boolean>(false);
  const [passwordForm] = Form.useForm();

  useEffect(() => {
    void loadInitialData();
  }, []);

  const loadInitialData = async () => {
    setLoading(true);
    try {
      const userList = await LoginUserService.getUsers();
      setUsers(userList);
      setFilteredData(userList);
    } catch (error) {
      message.error("初期データの読み込みに失敗しました");
    } finally {
      setLoading(false);
    }
  };

  const createDefaultValues = () => {
    return {
      displayName: "",
      email: "",
      password: "",
      role: STANDARD_ROLE as UserRole,
      phoneNumber: "",
      Others: "",
    };
  };

  const handleOpenModal = (mode: "create" | "edit", user?: LoginUser) => {
    form.resetFields();
    setModalMode(mode);

    if (mode === "edit" && user) {
      setSelectedUser(user);
      form.setFieldsValue({ ...user });
    } else {
      setSelectedUser(null);
      form.setFieldsValue(createDefaultValues());
    }

    setIsModalVisible(true);
  };

  const handleFormSubmit = async (values: CreateUserRequest | UpdateUserRequest) => {
    setLoading(true);
    try {
      const payload = { ...values };
      if (modalMode === "create") {
        await LoginUserService.createUser(payload as CreateUserRequest);
        message.success("ユーザーを作成しました");
      } else if (selectedUser) {
        await LoginUserService.updateUser(selectedUser.id, payload as UpdateUserRequest);
        message.success("ユーザー情報を更新しました");
      }
      setIsModalVisible(false);
      await loadInitialData();
    } catch (error: any) {
      message.error(error.message || "処理に失敗しました");
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async (values: { newPassword: string }) => {
    if (!selectedUser) return;
    setLoading(true);
    try {
      await LoginUserService.updateUserPassword(selectedUser.id, values.newPassword);
      message.success("パスワードを更新しました");
      setIsPasswordModalVisible(false);
    } catch (error: any) {
      message.error(error.message || "パスワードの更新に失敗しました");
    } finally {
      setLoading(false);
      passwordForm.resetFields();
    }
  };

  const handleDeleteUser = async (user: LoginUser) => {
    setLoading(true);
    try {
      await LoginUserService.deleteUser(user.id);
      message.success("ユーザーを削除しました");
      await loadInitialData();
    } catch (error) {
      message.error("ユーザーの削除に失敗しました");
    } finally {
      setLoading(false);
    }
  };

  const applyFilter = () => {
    let filtered = users;
    if (filterConditions.email) {
      filtered = filtered.filter((u) =>
        u.email.toLowerCase().includes(filterConditions.email.toLowerCase())
      );
    }
    if (filterConditions.displayName) {
      filtered = filtered.filter((u) =>
        (u.displayName ?? "")
          .toLowerCase()
          .includes(filterConditions.displayName.toLowerCase())
      );
    }
    if (filterConditions.userId) {
      filtered = filtered.filter((u) =>
        (u.id ?? "").toLowerCase().includes(filterConditions.userId.toLowerCase())
      );
    }
    if (filterConditions.role) {
      filtered = filtered.filter((u) => u.role === filterConditions.role);
    }
    setFilteredData(filtered);
    setIsFilterDialogVisible(false);
    message.success(`フィルタを適用しました。${filtered.length}件が表示されます。`);
  };

  const clearAllFilters = () => {
    setFilteredData(users);
    setFilterConditions({ email: "", displayName: "", userId: "", role: null });
    message.success("フィルタを解除しました。");
  };

  const columns: ColumnsType<LoginUser> = [
    {
      title: "ユーザー名",
      dataIndex: "displayName",
      key: "displayName",
      width: 160,
      ellipsis: true,
      render: (_, record) => record.displayName ?? "-",
      sorter: (a, b) =>
        (a.displayName ?? "").localeCompare(b.displayName ?? ""),
    },
    {
      title: "ユーザーID",
      dataIndex: "id",
      key: "userId",
      width: 220,
      ellipsis: true,
      render: (_, record) => record.id ?? "-",
      sorter: (a, b) => (a.id ?? "").localeCompare(b.id ?? ""),
    },
    {
      title: "メールアドレス",
      dataIndex: "email",
      key: "email",
      width: 220,
      ellipsis: true,
      sorter: (a, b) => a.email.localeCompare(b.email),
    },
    {
      title: "電話番号",
      dataIndex: "phoneNumber",
      key: "phoneNumber",
      width: 140,
      ellipsis: true,
      responsive: ["md"],
      render: (value) => value || "-",
    },
    {
      title: "ロール",
      dataIndex: "role",
      key: "role",
      width: 110,
      render: (role: UserRole) => <Tag color={roleMap[role]?.color}>{roleMap[role]?.name || role}</Tag>,
    },
    {
      title: "更新日時",
      dataIndex: "updatedAt",
      key: "updatedAt",
      width: 170,
      responsive: ["lg"],
      render: (ts) => (ts ? new Date(ts).toLocaleString("ja-JP") : "-"),
      sorter: (a, b) => (b.updatedAt ?? 0) - (a.updatedAt ?? 0),
    },
    {
      title: "操作",
      key: "actions",
      width: 110,
      fixed: "right",
      render: (_, record) => (
        <Space>
          <Button icon={<EditOutlined />} onClick={() => handleOpenModal("edit", record)} />
          <Popconfirm
            title="ユーザーを削除しますか？"
            description="Firestore(loginusers)とAuthenticationから削除されます。"
            onConfirm={() => void handleDeleteUser(record)}
            okText="削除"
            cancelText="キャンセル"
            okType="danger"
          >
            <Button danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
        <div style={{ marginBottom: "12px", color: "#595959", lineHeight: 1.6 }}>
          <div>
            ・Authentication の補足情報です。（名前や Role などを設定します。）Loginusers を新規作成すると同時に
            Authentication も作成されます。
          </div>
          <div>・Role は admin と standard だけです。admin は管理Webにログインできます。</div>
        </div>
        <div
          style={{
            marginBottom: "16px",
            display: "flex",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: "8px",
          }}
        >
          <Space>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => handleOpenModal("create")}>
              新規ユーザー作成
            </Button>
            <Button icon={<ReloadOutlined />} onClick={() => void loadInitialData()} loading={loading}>
              更新
            </Button>
          </Space>
          <Space>
            <Button onClick={() => setIsFilterDialogVisible(true)}>フィルタ</Button>
            <Button onClick={clearAllFilters}>フィルタ解除</Button>
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <span>最大件数:</span>
              <Select value={maxRecords} style={{ width: 100 }} onChange={(value) => setMaxRecords(value)}>
                <Option value={10}>10件</Option>
                <Option value={25}>25件</Option>
                <Option value={50}>50件</Option>
                <Option value={100}>100件</Option>
                <Option value={-1}>全件</Option>
              </Select>
            </div>
          </Space>
        </div>

        <Table
          columns={columns}
          dataSource={filteredData}
          rowKey="id"
          loading={loading}
          scroll={{ x: 1000 }}
          style={{ width: "100%" }}
          pagination={{
            pageSize: maxRecords === -1 ? filteredData.length : maxRecords,
            showSizeChanger: false,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
          }}
        />

      <Modal
        title={modalMode === "create" ? "新規ユーザー作成" : "ユーザー情報編集"}
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        footer={null}
        width={680}
        styles={{ body: { maxWidth: "100%" } }}
        style={{ top: 20, maxWidth: "calc(100vw - 24px)" }}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={(values) => void handleFormSubmit(values)}>
          {modalMode === "edit" && (
            <Form.Item label="ユーザーID">
              <Input value={selectedUser?.id ?? ""} disabled />
            </Form.Item>
          )}
          <Form.Item label="ユーザー名" name="displayName" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item
            label="メールアドレス（作成後は変更不可）"
            name="email"
            rules={[{ required: true, type: "email" }]}
          >
            <Input disabled={modalMode === "edit"} autoComplete="off" />
          </Form.Item>
          {modalMode === "create" && (
            <Form.Item label="パスワード" name="password" rules={[{ required: true, min: 4 }]}>
              <Input.Password autoComplete="new-password" />
            </Form.Item>
          )}
          <Form.Item label="ロール" name="role" rules={[{ required: true }]}>
            <Select>
              {Object.entries(roleMap).map(([key, { name }]) => (
                <Option key={key} value={key}>
                  {name}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item label="電話番号" name="phoneNumber">
            <Input />
          </Form.Item>

          <Form.Item name="Others" label="備考">
            <Input.TextArea rows={3} />
          </Form.Item>

          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "24px" }}>
            <div>
              {modalMode === "edit" && (
                <Button type="default" onClick={() => setIsPasswordModalVisible(true)}>
                  パスワードを変更
                </Button>
              )}
            </div>
            <Space>
              <Button onClick={() => setIsModalVisible(false)}>キャンセル</Button>
              <Button type="primary" htmlType="submit" loading={loading}>
                保存
              </Button>
            </Space>
          </div>
        </Form>
      </Modal>

      <Modal
        title="パスワード変更"
        open={isPasswordModalVisible}
        onCancel={() => setIsPasswordModalVisible(false)}
        footer={null}
        destroyOnClose
      >
        <Form form={passwordForm} layout="vertical" onFinish={(values) => void handlePasswordChange(values)}>
          <p>
            ユーザー: <strong>{selectedUser?.displayName} ({selectedUser?.email})</strong>
          </p>
          <Form.Item
            label="新しいパスワード"
            name="newPassword"
            rules={[{ required: true, message: "4文字以上のパスワードを入力してください", min: 4 }]}
          >
            <Input.Password />
          </Form.Item>
          <Form.Item
            label="新しいパスワード（確認）"
            name="confirmPassword"
            dependencies={["newPassword"]}
            rules={[
              { required: true, message: "確認用パスワードを入力してください" },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue("newPassword") === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error("パスワードが一致しません"));
                },
              }),
            ]}
          >
            <Input.Password />
          </Form.Item>
          <div style={{ textAlign: "right", marginTop: "24px" }}>
            <Space>
              <Button onClick={() => setIsPasswordModalVisible(false)}>キャンセル</Button>
              <Button type="primary" htmlType="submit" loading={loading}>
                更新
              </Button>
            </Space>
          </div>
        </Form>
      </Modal>

      <Modal
        title="フィルタ条件設定"
        open={isFilterDialogVisible}
        onCancel={() => setIsFilterDialogVisible(false)}
        footer={null}
        width={500}
      >
        <div style={{ marginBottom: 16 }}>
          <label>メールアドレス:</label>
          <Input
            value={filterConditions.email}
            onChange={(e) => setFilterConditions({ ...filterConditions, email: e.target.value })}
            placeholder="メールアドレスで検索"
            style={{ marginTop: 4 }}
          />
        </div>
        <div style={{ marginBottom: 16 }}>
          <label>ユーザー名:</label>
          <Input
            value={filterConditions.displayName}
            onChange={(e) => setFilterConditions({ ...filterConditions, displayName: e.target.value })}
            placeholder="ユーザー名で検索"
            style={{ marginTop: 4 }}
          />
        </div>
        <div style={{ marginBottom: 16 }}>
          <label>ユーザーID:</label>
          <Input
            value={filterConditions.userId}
            onChange={(e) => setFilterConditions({ ...filterConditions, userId: e.target.value })}
            placeholder="ユーザーIDで検索"
            style={{ marginTop: 4 }}
          />
        </div>
        <div style={{ marginBottom: 20 }}>
          <label>ロール:</label>
          <Select
            value={filterConditions.role}
            onChange={(value) => setFilterConditions({ ...filterConditions, role: value })}
            placeholder="ロールで検索"
            style={{ marginTop: 4, width: "100%" }}
            allowClear
          >
            {Object.entries(roleMap).map(([key, { name }]) => (
              <Option key={key} value={key}>
                {name}
              </Option>
            ))}
          </Select>
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px" }}>
          <Button onClick={() => setIsFilterDialogVisible(false)}>キャンセル</Button>
          <Button type="primary" onClick={applyFilter}>
            フィルタ適用
          </Button>
        </div>
      </Modal>
    </>
  );
};

export default Master_LoginUser;
